import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { CheckCircle, Users, Award, Clock, Zap } from 'lucide-react';
import { sendButtonClickEvent, sendScrollToTopEvent } from '../../utils/postMessageBridge';
import ModalContactForm from '../forms/ModalContactForm';


const CompanyAdvantagesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('company-advantages');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const openFormModal = () => {
    sendButtonClickEvent('COMPANY_ADVANTAGES_BERATUNGSTERMIN', {
      section: 'company-advantages',
      action: 'open-modal'
    });
    
    // Mobile: Sofort öffnen
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      setFormModalOpen(true);
    } else {
      // Desktop: Scroll nach oben, dann Modal öffnen
      sendScrollToTopEvent();
      try {
        window.parent.postMessage({ type: 'SCROLL_TO_TOP' }, '*');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        console.log('PostMessage failed:', e);
      }
      
      setTimeout(() => {
        setFormModalOpen(true);
      }, 400);
    }
  };

  return (
    <section id="company-advantages" className="py-20 bg-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-20 w-96 h-96 rounded-full" 
             style={{backgroundColor: '#91d33d', filter: 'blur(60px)'}}></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 relative z-50">
            Ihre Vorteile
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Als etablierter Meisterbetrieb für nachhaltige Energie in Berlin & Brandenburg bieten wir Ihnen seit über 13 Jahren höchste Qualität und zuverlässigen Service.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {[
            {
              icon: CheckCircle,
              title: "Voller Lagerbestand, keine Wartezeiten",
              description: "Alle gängigen Wärmepumpen-Modelle direkt verfügbar",
              delay: "delay-100"
            },
            {
              icon: Award,
              title: "Qualitätsführer aus Brandenburg",
              description: "Über 13 Jahre Erfahrung und 2.000+ erfolgreiche Projekte",
              delay: "delay-200"
            },
            {
              icon: Clock,
              title: "Schnelle & zuverlässige Montage",
              description: "Professionelle Installation durch zertifizierte Meisterbetrieb",
              delay: "delay-300"
            },
            {
              icon: Users,
              title: "Persönliche Beratung vor Ort",
              description: "Individuelle Planung und maßgeschneiderte Lösungen",
              delay: "delay-[400ms]"
            }
          ].map((advantage, index) => (
            <div key={index} className={`transition-all duration-1000 ${
              isVisible ? `opacity-100 translate-y-0 ${advantage.delay}` : 'opacity-0 translate-y-10'
            }`}>
              <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 h-full border-2 border-gray-100 hover:border-brand-green/30">
                <div className="w-14 h-14 bg-gradient-to-br from-brand-green to-brand-green-hover rounded-xl flex items-center justify-center mb-4">
                  <advantage.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {advantage.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {advantage.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className={`grid md:grid-cols-4 gap-8 mb-12 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {[
            { number: "2.000+", label: "Zufriedene Kunden" },
            { number: "13+", label: "Jahre Erfahrung" },
            { number: "100%", label: "Meisterbetrieb" },
            { number: "Tesla", label: "Partner" }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-brand-green mb-2">
                {stat.number}
              </div>
              <div className="text-gray-600 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        <div className={`text-center transition-all duration-1000 delay-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <Button
            onClick={openFormModal}
            size="lg"
            className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Beratungstermin
          </Button>
          <p className="text-sm text-gray-500 mt-4">
            Kostenlose Beratung • Unverbindlich • Schnelle Rückmeldung
          </p>
        </div>
      </div>
      
          <ModalContactForm 
        isOpen={formModalOpen} 
        onClose={() => setFormModalOpen(false)} 
      />
    </section>
  );
};

export default CompanyAdvantagesSection;